import * as React from "react"

import { ConnectBrokerImportMethodCard } from "@/components/Broker/ConnectBrokerImportMethodCard"
import type { BrokerCapabilities } from "@/services/brokerIntegrationApi"
import { TRADINGVIEW_BROKER_SLUG } from "@/utils/tradingViewBroker"
import {
  defaultTradingViewLane,
  findTradingViewLaneCapability,
  tradingViewLaneTabLabel,
  TRADINGVIEW_CONNECT_BRAND,
  type TradingViewLaneSlug,
} from "@/utils/tradingViewConnectBrand"
import { TRADINGVIEW_PAPER_BROKER_SLUG } from "@/utils/tradingViewPaperBroker"
import { CONNECT_BROKER_FILE_UPLOAD_OPTION } from "@/views/Dashboard/Profile/connectBroker/connectBrokerImportMethods"
import { ConnectBrokerSelectedBrokerHeading } from "@/views/Dashboard/Profile/connectBroker/ConnectBrokerSelectedBrokerHeading"

type TradingViewLaneOption = {
  lane: TradingViewLaneSlug
  title: string
  description: string
}

const SECTION_LABEL_CLASS =
  "font-[family-name:var(--font-primary)] text-sm font-normal text-[color:var(--brand-tropical-surf)]"

const SECTION_DESC_CLASS =
  "font-[family-name:var(--font-primary)] text-sm font-normal leading-5 text-gray-500 dark:text-white"

export type ConnectBrokerTradingViewLaneStepProps = {
  brokers: BrokerCapabilities[]
  selectedLane: TradingViewLaneSlug | null
  onSelectLane: (lane: TradingViewLaneSlug) => void
}

export function ConnectBrokerTradingViewLaneStep({
  brokers,
  selectedLane,
  onSelectLane,
}: ConnectBrokerTradingViewLaneStepProps): React.ReactElement {
  const laneOptions = React.useMemo((): TradingViewLaneOption[] => {
    const options: TradingViewLaneOption[] = []
    if (findTradingViewLaneCapability(brokers, TRADINGVIEW_PAPER_BROKER_SLUG)) {
      options.push({
        lane: TRADINGVIEW_PAPER_BROKER_SLUG,
        title: tradingViewLaneTabLabel(TRADINGVIEW_PAPER_BROKER_SLUG),
        description:
          "Import Paper Trading → Order History CSV. Symbols come from the file; not Strategy Tester.",
      })
    }
    if (findTradingViewLaneCapability(brokers, TRADINGVIEW_BROKER_SLUG)) {
      options.push({
        lane: TRADINGVIEW_BROKER_SLUG,
        title: tradingViewLaneTabLabel(TRADINGVIEW_BROKER_SLUG),
        description:
          "Import Backtest → List of trades CSV. Set Instrument and chart timezone to match your chart.",
      })
    }
    return options
  }, [brokers])

  React.useEffect(() => {
    if (!selectedLane) {
      const fallback = defaultTradingViewLane(brokers)
      if (fallback) onSelectLane(fallback)
    }
  }, [brokers, onSelectLane, selectedLane])

  return (
    <div className="flex w-full min-w-0 flex-col gap-6" data-connect-tradingview-lane="">
      <ConnectBrokerSelectedBrokerHeading
        brokerName={TRADINGVIEW_CONNECT_BRAND}
        displayName="TradingView"
        title="Choose TradingView path"
        description="Paper Trading and Backtesting use different CSV exports. Pick the path that matches your file."
      />

      {laneOptions.length === 0 ? (
        <p className="py-6 text-center font-[family-name:var(--font-primary)] text-sm text-muted-foreground">
          TradingView import paths are not available yet.
        </p>
      ) : (
        <div
          className="grid w-full min-w-0 grid-cols-1 gap-3 sm:grid-cols-2"
          data-connect-tradingview-lane-grid=""
          role="listbox"
          aria-label="TradingView paths"
        >
          {laneOptions.map((option) => (
            <ConnectBrokerImportMethodCard
              key={option.lane}
              methodId={option.lane}
              title={option.title}
              description={option.description}
              icon="file_upload"
              selected={selectedLane === option.lane}
              onSelect={() => onSelectLane(option.lane)}
            />
          ))}
        </div>
      )}

      <div className="flex w-full min-w-0 flex-col gap-2">
        <h3 className={SECTION_LABEL_CLASS}>Select import method</h3>
        <p className={SECTION_DESC_CLASS}>TradingView imports via file upload only.</p>
      </div>

      <div
        className="grid w-full min-w-0 grid-cols-1 gap-3 sm:grid-cols-2"
        data-connect-tradingview-import-method=""
      >
        <ConnectBrokerImportMethodCard
          methodId={CONNECT_BROKER_FILE_UPLOAD_OPTION.id}
          title={CONNECT_BROKER_FILE_UPLOAD_OPTION.title}
          description={CONNECT_BROKER_FILE_UPLOAD_OPTION.description}
          icon="file_upload"
          selected
          interactive={false}
        />
      </div>
    </div>
  )
}
